/**
 * Fixed-point decimal arithmetic.
 *
 * Master Specification section 9 requires decimal arithmetic for money and
 * quantities; section 40 and CLAUDE.md forbid floating point. `0.1 + 0.2` is
 * `0.30000000000000004` in IEEE-754, and thirty items at R$ 0,07 is off by a
 * centavo — which is the difference between an NF-e SEFAZ authorises and one it
 * rejects, because a document's total must equal the sum of its lines exactly.
 *
 * A value is an integer count of units at a declared scale: R$ 1.234,56 is
 * `{ units: 123456n, scale: 2 }`. There is no representation error to reason
 * about because there is no fraction in the representation.
 *
 * `bigint` rather than `number` even for the units: revenue at scale 6 passes
 * 2^53 at around nine billion reais, and exceeding it is silent.
 *
 * See ADR-0016.
 */
const MAX_SCALE = 12;
export class DecimalError extends Error {
    constructor(message) {
        super(message);
        this.name = 'DecimalError';
    }
}
const assertScale = (scale) => {
    if (!Number.isInteger(scale) || scale < 0 || scale > MAX_SCALE) {
        throw new DecimalError(`Scale must be an integer between 0 and ${String(MAX_SCALE)}`);
    }
};
const pow10 = (exponent) => 10n ** BigInt(exponent);
export const decimal = (units, scale) => {
    assertScale(scale);
    return { units, scale };
};
export const zero = (scale) => decimal(0n, scale);
/**
 * Parses `"1234.56"`, `"-0.5"`, `"12"`.
 *
 * Deliberately strict: no thousands separators, no currency symbol, no comma.
 * This reads machine formats — a database column, a JSON body — and a lenient
 * parser here would quietly accept `"1.234,56"` as `1.234`. Human input is
 * `parseHumanAmount`, which is a different problem with different rules.
 */
export const fromString = (value, scale) => {
    const text = value.trim();
    if (!/^-?\d+(\.\d+)?$/.test(text)) {
        throw new DecimalError(`Not a decimal: ${value}`);
    }
    const negative = text.startsWith('-');
    const [whole = '0', fraction = ''] = (negative ? text.slice(1) : text).split('.');
    const parsedScale = scale ?? fraction.length;
    assertScale(parsedScale);
    const digits = fraction.length <= parsedScale
        ? fraction.padEnd(parsedScale, '0')
        : fraction.slice(0, parsedScale);
    const units = BigInt(whole + digits);
    // A requested scale shorter than the input would silently truncate, so the
    // caller is told instead. Rounding is always an explicit call.
    if (scale !== undefined && fraction.length > scale) {
        const dropped = fraction.slice(scale);
        if (/[1-9]/.test(dropped)) {
            throw new DecimalError(`${value} does not fit in scale ${String(scale)} without rounding`);
        }
    }
    return { units: negative ? -units : units, scale: parsedScale };
};
/** The machine format: a point, no separators. What the wire and the database carry. */
export const toString = (value) => {
    const negative = value.units < 0n;
    const digits = (negative ? -value.units : value.units)
        .toString()
        .padStart(value.scale + 1, '0');
    const whole = digits.slice(0, digits.length - value.scale);
    const fraction = value.scale === 0 ? '' : `.${digits.slice(-value.scale)}`;
    return `${negative ? '-' : ''}${whole}${fraction}`;
};
export const toNumber = (value) => Number(value.units) / Number(pow10(value.scale));
/** Brings two values to the same scale without losing anything: the larger wins. */
const align = (left, right) => {
    const scale = Math.max(left.scale, right.scale);
    return [
        left.units * pow10(scale - left.scale),
        right.units * pow10(scale - right.scale),
        scale,
    ];
};
export const add = (left, right) => {
    const [a, b, scale] = align(left, right);
    return { units: a + b, scale };
};
export const subtract = (left, right) => {
    const [a, b, scale] = align(left, right);
    return { units: a - b, scale };
};
export const negate = (value) => ({
    units: -value.units,
    scale: value.scale,
});
export const abs = (value) => ({
    units: value.units < 0n ? -value.units : value.units,
    scale: value.scale,
});
/**
 * Exact. The scales add, so 1,50 × 2,00 is 3,0000 at scale 4.
 *
 * Reducing that back to a document scale is a separate, named call — see
 * `round`. A multiply that quietly rounded would be the place a centavo goes
 * missing.
 */
export const multiply = (left, right) => {
    const scale = left.scale + right.scale;
    if (scale > MAX_SCALE) {
        // Reducing precision without being asked is exactly what this module
        // exists to prevent, so it refuses instead.
        throw new DecimalError(`Multiplying scale ${String(left.scale)} by scale ${String(right.scale)} exceeds the maximum scale`);
    }
    return { units: left.units * right.units, scale };
};
/**
 * Division cannot be exact, so the caller states the scale and the rule.
 *
 * There is no default. A default rounding rule is a rule nobody chose, and
 * Brazilian fiscal rounding is not uniform across ICMS, PIS/COFINS and the
 * document total.
 */
export const divide = (left, right, scale, mode) => {
    assertScale(scale);
    if (right.units === 0n)
        throw new DecimalError('Division by zero');
    // Scaled up before dividing so the remainder is available to round with.
    const numerator = left.units * pow10(scale + right.scale);
    const denominator = right.units * pow10(left.scale);
    return { units: divideRounded(numerator, denominator, mode), scale };
};
const divideRounded = (numerator, denominator, mode) => {
    const negative = numerator < 0n !== denominator < 0n;
    const a = numerator < 0n ? -numerator : numerator;
    const b = denominator < 0n ? -denominator : denominator;
    const quotient = a / b;
    const remainder = a % b;
    if (remainder === 0n)
        return negative ? -quotient : quotient;
    const twice = remainder * 2n;
    let rounded = quotient;
    switch (mode) {
        case 'down':
            break;
        case 'half-up':
            if (twice >= b)
                rounded += 1n;
            break;
        case 'half-even':
            if (twice > b || (twice === b && quotient % 2n === 1n))
                rounded += 1n;
            break;
    }
    return negative ? -rounded : rounded;
};
/**
 * Changes the scale, rounding when it shrinks.
 *
 * Growing the scale is exact and always safe; shrinking it is where a value is
 * actually decided, which is why the rule is a required argument.
 */
export const round = (value, scale, mode) => {
    assertScale(scale);
    if (scale === value.scale)
        return value;
    if (scale > value.scale) {
        return { units: value.units * pow10(scale - value.scale), scale };
    }
    return {
        units: divideRounded(value.units, pow10(value.scale - scale), mode),
        scale,
    };
};
export const compare = (left, right) => {
    const [a, b] = align(left, right);
    if (a < b)
        return -1;
    if (a > b)
        return 1;
    return 0;
};
export const equals = (left, right) => compare(left, right) === 0;
export const isZero = (value) => value.units === 0n;
export const isNegative = (value) => value.units < 0n;
export const isPositive = (value) => value.units > 0n;
export const sum = (values, scale) => values.reduce((total, value) => add(total, value), zero(scale));
export const min = (left, right) => compare(left, right) <= 0 ? left : right;
export const max = (left, right) => compare(left, right) >= 0 ? left : right;
/**
 * Distributes a total across shares so the parts sum **exactly** to the whole.
 *
 * R$ 100,00 across three lines is 33,34 + 33,33 + 33,33 — not 33,33 three
 * times, which is 99,99. That missing centavo is the classic ERP defect: a
 * header discount spread across lines that no longer sums to the discount, and
 * a document SEFAZ refuses.
 *
 * The remainder goes to the largest shares first, and ties to the earliest, so
 * the result is deterministic — the same inputs must produce the same document
 * every time it is recalculated.
 *
 * No decimal library ships this, which is most of why this package exists.
 */
export const allocate = (total, weights) => {
    if (weights.length === 0)
        throw new DecimalError('Nothing to allocate across');
    const scale = total.scale;
    const weightScale = weights.reduce((highest, weight) => Math.max(highest, weight.scale), 0);
    const scaled = weights.map((weight) => weight.units * pow10(weightScale - weight.scale));
    const totalWeight = scaled.reduce((running, weight) => running + weight, 0n);
    if (totalWeight === 0n) {
        // Every share is zero: an equal split is the only defensible reading, and
        // the alternative — dividing by zero — is not an answer.
        return allocate(total, weights.map(() => decimal(1n, 0)));
    }
    // Floor first, then hand out what is left one unit at a time. Rounding each
    // share independently is what loses the centavo.
    const parts = scaled.map((weight) => (total.units * weight) / totalWeight);
    const distributed = parts.reduce((running, part) => running + part, 0n);
    let remainder = total.units - distributed;
    const order = scaled
        .map((weight, index) => ({ weight, index }))
        .sort((left, right) => left.weight === right.weight
        ? left.index - right.index
        : right.weight > left.weight
            ? 1
            : -1);
    const step = remainder < 0n ? -1n : 1n;
    let cursor = 0;
    while (remainder !== 0n && order.length > 0) {
        const target = order[cursor % order.length];
        if (target !== undefined) {
            parts[target.index] = (parts[target.index] ?? 0n) + step;
            remainder -= step;
        }
        cursor += 1;
    }
    return parts.map((units) => ({ units, scale }));
};
//# sourceMappingURL=decimal.js.map